import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Grid,
    IconButton,
    InputAdornment,
    LinearProgress,
    MenuItem,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
    DataGridPremium,
    GridColDef,
    GridPaginationModel,
    GridRowSelectionModel,
    GridSortModel,
} from '@mui/x-data-grid-premium';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CloseIcon from '@mui/icons-material/Close';
import {
    AdminFunnelDashboardStats,
    EligibleSponsor,
    FunnelStage,
    FUNNEL_STAGES,
    FUNNEL_STAGE_MAP,
    SalesFunnelProspect,
    SponsorStat,
    FIYRPOD_GREEN,
    FIYRPOD_DARK,
    FIYRPOD_BORDER,
    FIYRPOD_MUTED,
    FIYRPOD_ORANGE,
} from '../../../../networking/salesFunnel.types';
import {
    getAdminSalesFunnelDashboard,
    getAdminSalesFunnelProspects,
    getEligibleSalesFunnelSponsors,
    AdminGetProspectsParams,
} from '../../../../networking/salesFunnel.api';
import AdminAddProspectDialog from '../components/AdminAddProspectDialog';

const formatCurrency = (value: number): string =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);

// Shared dark-theme DataGrid sx — force transparent bg throughout so the
// parent Paper's dark navy gradient shows through everywhere.
const darkGridSx = {
    border: 'none',
    backgroundColor: 'transparent !important',
    color: '#d0dae8',
    // The main content area — MUI renders a white Paper here by default
    '& .MuiDataGrid-main': {
        backgroundColor: 'transparent !important',
    },
    // The virtual scrollable container
    '& .MuiDataGrid-virtualScroller': {
        backgroundColor: 'transparent !important',
    },
    '& .MuiDataGrid-virtualScrollerContent': {
        backgroundColor: 'transparent !important',
    },
    '& .MuiDataGrid-virtualScrollerRenderZone': {
        backgroundColor: 'transparent !important',
    },
    // Each row — transparent so dark Paper bleeds through
    '& .MuiDataGrid-row': {
        backgroundColor: 'transparent !important',
        color: '#d0dae8',
    },
    '& .MuiDataGrid-row:hover': {
        backgroundColor: 'rgba(118,196,66,0.07) !important',
    },
    // Cell text + borders
    '& .MuiDataGrid-cell': {
        color: '#d0dae8',
        borderColor: FIYRPOD_BORDER,
        backgroundColor: 'transparent !important',
    },
    // Column header background + text
    '& .MuiDataGrid-columnHeaders': {
        backgroundColor: 'rgba(0,0,0,0.35) !important',
        borderColor: FIYRPOD_BORDER,
    },
    '& .MuiDataGrid-columnHeader': {
        backgroundColor: 'transparent !important',
        color: FIYRPOD_MUTED,
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        color: `${FIYRPOD_MUTED} !important`,
        fontWeight: 600,
        fontSize: '0.72rem',
        letterSpacing: '0.03em',
    },
    // Sort & menu icons
    '& .MuiDataGrid-sortIcon': { color: FIYRPOD_GREEN },
    '& .MuiDataGrid-menuIconButton': { color: FIYRPOD_MUTED },
    '& .MuiDataGrid-columnSeparator': { color: FIYRPOD_BORDER },
    // Footer / pagination
    '& .MuiDataGrid-footerContainer': {
        borderColor: FIYRPOD_BORDER,
        backgroundColor: 'rgba(0,0,0,0.25) !important',
    },
    '& .MuiTablePagination-root': { color: FIYRPOD_MUTED },
    '& .MuiTablePagination-selectLabel': { color: FIYRPOD_MUTED },
    '& .MuiTablePagination-displayedRows': { color: FIYRPOD_MUTED },
    '& .MuiTablePagination-select': { color: '#d0dae8' },
    '& .MuiTablePagination-actions .MuiIconButton-root': { color: FIYRPOD_MUTED },
    // Checkbox
    '& .MuiCheckbox-root': { color: FIYRPOD_MUTED },
    '& .MuiCheckbox-root.Mui-checked': { color: FIYRPOD_GREEN },
    // Selected rows
    '& .Mui-selected': {
        backgroundColor: `${FIYRPOD_GREEN}18 !important`,
    },
    // Loading + empty overlay
    '& .MuiDataGrid-overlay': {
        backgroundColor: 'rgba(13,27,46,0.85) !important',
        color: FIYRPOD_MUTED,
    },
    // Filler rows (bottom of short grids) + pinned cols
    '& .MuiDataGrid-filler': { backgroundColor: 'transparent !important' },
    '& .MuiDataGrid-pinnedColumns': { backgroundColor: `${FIYRPOD_DARK} !important` },
};

const formatMonth = (ym: string): string => {
    const [year, month] = ym.split('-');
    return new Date(Number(year), Number(month) - 1).toLocaleDateString('en-US', {
        month: 'short',
        year: '2-digit',
    });
};

// ─── Comparison Panel ────────────────────────────────────────────────────────

interface ComparisonPanelProps {
    sponsors: SponsorStat[];
    onClose: () => void;
}

const COMPARISON_METRICS: Array<{
    key: keyof SponsorStat;
    label: string;
    format: (v: number | null) => string;
    higherIsBetter: boolean;
}> = [
    {
        key: 'active_prospects',
        label: 'Active Prospects',
        format: (v) => String(v ?? 0),
        higherIsBetter: true,
    },
    {
        key: 'closed_count',
        label: 'Closed Deals',
        format: (v) => String(v ?? 0),
        higherIsBetter: true,
    },
    {
        key: 'pipeline_value',
        label: 'Pipeline Value',
        format: (v) => formatCurrency(v ?? 0),
        higherIsBetter: true,
    },
    {
        key: 'conversion_rate',
        label: 'Conversion Rate',
        format: (v) => `${v ?? 0}%`,
        higherIsBetter: true,
    },
    {
        key: 'avg_days_to_close',
        label: 'Avg Days to Close',
        format: (v) => (v != null ? `${v}d` : '—'),
        higherIsBetter: false,
    },
    {
        key: 'stale_count',
        label: 'Stale Prospects',
        format: (v) => String(v ?? 0),
        higherIsBetter: false,
    },
    {
        key: 'lost_count',
        label: 'Lost Deals',
        format: (v) => String(v ?? 0),
        higherIsBetter: false,
    },
];

// FiyrPod-inspired sponsor comparison palette — lime green lead + bold accents
const SPONSOR_COLORS = [FIYRPOD_GREEN, '#e85d04', '#38b2f5', '#e8c93a', '#c85aee', '#38e8b5'];

const ComparisonPanel: React.FC<ComparisonPanelProps> = ({ sponsors, onClose }) => {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                mb: 3,
                background: `linear-gradient(135deg, ${FIYRPOD_DARK} 0%, #132238 100%)`,
                border: `2px solid ${FIYRPOD_GREEN}40`,
                borderRadius: 2,
            }}
        >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                    <CompareArrowsIcon sx={{ color: FIYRPOD_GREEN }} />
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#fff' }}>
                        Sponsor Comparison
                    </Typography>
                    <Typography variant="caption" sx={{ color: FIYRPOD_MUTED }}>
                        ({sponsors.length} sponsors selected)
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ color: FIYRPOD_MUTED }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Sponsor name headers */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: `180px repeat(${sponsors.length}, 1fr)`,
                    gap: 1,
                    mb: 1.5,
                }}
            >
                <Box />
                {sponsors.map((s, i) => (
                    <Box key={s.organization_uuid} textAlign="center">
                        <Box
                            sx={{
                                display: 'inline-block',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                backgroundColor: SPONSOR_COLORS[i % SPONSOR_COLORS.length],
                                color: '#fff',
                            }}
                        >
                            <Typography variant="caption" fontWeight={700} noWrap>
                                {s.organization_name}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>

            {/* Metric rows */}
            {COMPARISON_METRICS.map((metric) => {
                const values = sponsors.map((s) => Number(s[metric.key] ?? 0));
                const maxVal = Math.max(...values, 1);

                return (
                    <Box
                        key={metric.key}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: `180px repeat(${sponsors.length}, 1fr)`,
                            gap: 1,
                            py: 0.75,
                            borderBottom: `1px solid ${FIYRPOD_BORDER}`,
                            '&:last-child': { borderBottom: 'none' },
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{ display: 'flex', alignItems: 'center', color: FIYRPOD_MUTED }}
                        >
                            {metric.label}
                        </Typography>
                        {sponsors.map((s, i) => {
                            const val = Number(s[metric.key] ?? 0);
                            const pct = maxVal > 0 ? (val / maxVal) * 100 : 0;
                            const color = SPONSOR_COLORS[i % SPONSOR_COLORS.length];
                            const isBest = metric.higherIsBetter
                                ? val === Math.max(...values)
                                : val === Math.min(...values) && val > 0;

                            return (
                                <Box key={s.organization_uuid} sx={{ px: 0.5 }}>
                                    <Box display="flex" justifyContent="space-between" mb={0.25}>
                                        <Typography
                                            variant="caption"
                                            fontWeight={700}
                                            sx={{ color: isBest ? color : '#d0dae8' }}
                                        >
                                            {metric.format(s[metric.key] as number | null)}
                                        </Typography>
                                        {isBest && (
                                            <Typography
                                                variant="caption"
                                                sx={{ color, fontSize: '0.6rem' }}
                                            >
                                                ★ best
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box
                                        sx={{
                                            height: 6,
                                            borderRadius: 3,
                                            backgroundColor: 'grey.200',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                height: '100%',
                                                width: `${pct}%`,
                                                backgroundColor: color,
                                                borderRadius: 3,
                                                transition: 'width 0.4s ease',
                                            }}
                                        />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                );
            })}

            {/* Stage breakdown per sponsor */}
            <Box mt={2}>
                <Typography variant="caption" sx={{ color: FIYRPOD_MUTED, fontWeight: 600 }}>
                    Stage Breakdown
                </Typography>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: `180px repeat(${sponsors.length}, 1fr)`,
                        gap: 1,
                        mt: 1,
                    }}
                >
                    <Box />
                    {sponsors.map((s) => (
                        <Box key={s.organization_uuid} display="flex" flexWrap="wrap" gap={0.5}>
                            {FUNNEL_STAGES.map((stage) => {
                                const count = s.stage_counts[stage.key] ?? 0;
                                if (count === 0) return null;
                                return (
                                    <Chip
                                        key={stage.key}
                                        label={`${stage.label.split('/')[0]}: ${count}`}
                                        size="small"
                                        sx={{
                                            backgroundColor: stage.color,
                                            fontSize: '0.65rem',
                                            height: 20,
                                            fontWeight: 600,
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Paper>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const AdminSalesFunnelDashboard: React.FC = () => {
    const [dashboardStats, setDashboardStats] = useState<AdminFunnelDashboardStats | null>(null);
    const [prospects, setProspects] = useState<SalesFunnelProspect[]>([]);
    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [stageFilter, setStageFilter] = useState<FunnelStage | ''>('');
    const [staleFilter, setStaleFilter] = useState(false);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: 25,
    });
    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [selectedSponsorIds, setSelectedSponsorIds] = useState<GridRowSelectionModel>([]);
    const [showComparison, setShowComparison] = useState(false);
    const [addProspectOpen, setAddProspectOpen] = useState(false);
    const [addProspectDefaultOrg, setAddProspectDefaultOrg] = useState<string | null>(null);
    const [eligibleSponsors, setEligibleSponsors] = useState<EligibleSponsor[]>([]);
    const [eligibleSponsorsLoaded, setEligibleSponsorsLoaded] = useState(false);

    const loadDashboard = useCallback(async () => {
        try {
            const res = await getAdminSalesFunnelDashboard();
            setDashboardStats(res.data);
        } catch (err) {
            console.error('Failed to load admin funnel dashboard', err);
        }
    }, []);

    const loadProspects = useCallback(async () => {
        setTableLoading(true);
        try {
            const params: AdminGetProspectsParams = {
                page: paginationModel.page + 1,
                per_page: paginationModel.pageSize,
                search: search || undefined,
                stage: stageFilter || undefined,
                stale: staleFilter || undefined,
            };
            const res = await getAdminSalesFunnelProspects(params);
            setProspects(res.data.data);
            setTotalRows(res.data.meta?.total || 0);
        } catch (err) {
            console.error('Failed to load admin prospects', err);
        } finally {
            setTableLoading(false);
        }
    }, [paginationModel, search, stageFilter, staleFilter]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            await Promise.all([loadDashboard(), loadProspects()]);
            setLoading(false);
        };
        init();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!loading) loadProspects();
    }, [paginationModel, stageFilter, staleFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const openAddProspectDialog = async (defaultOrgUuid: string | null = null) => {
        setAddProspectDefaultOrg(defaultOrgUuid);
        setAddProspectOpen(true);
        if (!eligibleSponsorsLoaded) {
            try {
                const res = await getEligibleSalesFunnelSponsors();
                setEligibleSponsors(res.data);
                setEligibleSponsorsLoaded(true);
            } catch (err) {
                console.error('Failed to load eligible sponsors', err);
            }
        }
    };

    const handleRefresh = async () => {
        setLoading(true);
        await Promise.all([loadDashboard(), loadProspects()]);
        setLoading(false);
    };

    const handleStageClick = (stage: FunnelStage) => {
        setStageFilter((prev) => (prev === stage ? '' : stage));
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };

    const selectedSponsors = useMemo(() => {
        if (!dashboardStats) return [];
        return dashboardStats.sponsor_stats.filter((s) =>
            selectedSponsorIds.includes(s.organization_uuid),
        );
    }, [selectedSponsorIds, dashboardStats]);

    // ── Sponsor Leaderboard columns ──────────────────────────────────────────
    const leaderboardColumns: GridColDef<SponsorStat>[] = [
        {
            field: 'organization_name',
            headerName: 'Sponsor',
            flex: 1.5,
            minWidth: 160,
            renderCell: (params) => (
                <Typography variant="body2" fontWeight={600} noWrap sx={{ color: '#e0e8f4' }}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'active_prospects',
            headerName: 'Active',
            width: 80,
            type: 'number',
            renderCell: (params) => (
                <Typography variant="body2" fontWeight={600} sx={{ color: '#e0e8f4' }}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'closed_count',
            headerName: 'Closed',
            width: 80,
            type: 'number',
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    sx={{
                        fontWeight: 700,
                        minWidth: 40,
                        backgroundColor: params.value > 0 ? FIYRPOD_GREEN : 'rgba(255,255,255,0.1)',
                        color: params.value > 0 ? '#0d1b2e' : FIYRPOD_MUTED,
                        border: params.value > 0 ? 'none' : `1px solid ${FIYRPOD_MUTED}40`,
                    }}
                />
            ),
        },
        {
            field: 'lost_count',
            headerName: 'Lost',
            width: 70,
            type: 'number',
            renderCell: (params) => (
                <Typography
                    variant="body2"
                    sx={{ color: params.value > 0 ? '#ef5350' : FIYRPOD_MUTED }}
                >
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'pipeline_value',
            headerName: 'Pipeline Value',
            width: 140,
            type: 'number',
            renderCell: (params) =>
                params.value > 0 ? (
                    <Typography variant="body2" fontWeight={700} sx={{ color: FIYRPOD_GREEN }}>
                        {formatCurrency(params.value)}
                    </Typography>
                ) : (
                    <Typography variant="body2" sx={{ color: FIYRPOD_MUTED }}>
                        —
                    </Typography>
                ),
        },
        {
            field: 'conversion_rate',
            headerName: 'Conv. %',
            width: 90,
            type: 'number',
            renderCell: (params) => (
                <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{
                        color:
                            params.value >= 20
                                ? FIYRPOD_GREEN
                                : params.value >= 10
                                ? '#f9a825'
                                : FIYRPOD_MUTED,
                    }}
                >
                    {params.value}%
                </Typography>
            ),
        },
        {
            field: 'avg_days_to_close',
            headerName: 'Avg Days',
            width: 100,
            type: 'number',
            renderCell: (params) => (
                <Typography variant="body2" sx={{ color: FIYRPOD_MUTED }}>
                    {params.value != null ? `${params.value}d` : '—'}
                </Typography>
            ),
        },
        {
            field: 'stale_count',
            headerName: 'Stale',
            width: 70,
            type: 'number',
            renderCell: (params) =>
                params.value > 0 ? (
                    <Chip
                        icon={<WarningAmberIcon sx={{ fontSize: '0.85rem !important' }} />}
                        label={params.value}
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ fontWeight: 700 }}
                    />
                ) : (
                    <Typography variant="body2" sx={{ color: FIYRPOD_MUTED }}>
                        0
                    </Typography>
                ),
        },
        {
            field: 'stage_counts',
            headerName: 'Stage Counts',
            width: 260,
            sortable: false,
            renderHeader: () => (
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                    px={1}
                >
                    {FUNNEL_STAGES.map((stage) => (
                        <Tooltip key={stage.key} title={stage.label} arrow>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.62rem',
                                    fontWeight: 700,
                                    color: stage.color,
                                    textAlign: 'center',
                                    flex: 1,
                                }}
                            >
                                {stage.label.slice(0, 2)}
                            </Typography>
                        </Tooltip>
                    ))}
                </Box>
            ),
            renderCell: (params) => {
                const counts = params.value as Partial<Record<FunnelStage, number>>;
                return (
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                        px={1}
                    >
                        {FUNNEL_STAGES.map((stage) => {
                            const count = counts[stage.key] ?? 0;
                            return (
                                <Tooltip key={stage.key} title={`${stage.label}: ${count}`} arrow>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: count > 0 ? 800 : 400,
                                            color: count > 0 ? stage.color : `${FIYRPOD_MUTED}40`,
                                            fontSize: '0.78rem',
                                            textAlign: 'center',
                                            flex: 1,
                                        }}
                                    >
                                        {count}
                                    </Typography>
                                </Tooltip>
                            );
                        })}
                    </Box>
                );
            },
        },
        {
            field: '_actions',
            headerName: '',
            width: 130,
            sortable: false,
            renderCell: (params) => (
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon sx={{ fontSize: '0.85rem !important' }} />}
                    onClick={(e) => {
                        e.stopPropagation();
                        openAddProspectDialog(params.row.organization_uuid);
                    }}
                    sx={{
                        fontSize: '0.7rem',
                        py: 0.25,
                        whiteSpace: 'nowrap',
                        borderColor: `${FIYRPOD_GREEN}60`,
                        color: FIYRPOD_GREEN,
                        '&:hover': {
                            borderColor: FIYRPOD_GREEN,
                            backgroundColor: `${FIYRPOD_GREEN}15`,
                        },
                    }}
                >
                    Add Prospect
                </Button>
            ),
        },
    ];

    // ── All Prospects table columns ──────────────────────────────────────────
    const prospectsColumns: GridColDef[] = [
        {
            field: 'prospect_name',
            headerName: 'Prospect',
            flex: 1.5,
            minWidth: 180,
            renderCell: (params) => (
                <Typography variant="body2" fontWeight={600} noWrap sx={{ color: '#e0e8f4' }}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'organization_uuid',
            headerName: 'Sponsor',
            flex: 1,
            minWidth: 140,
            renderCell: (params) => {
                const sponsor = dashboardStats?.sponsor_stats.find(
                    (s) => s.organization_uuid === params.value,
                );
                return (
                    <Typography variant="body2" noWrap sx={{ color: FIYRPOD_MUTED }}>
                        {sponsor?.organization_name ??
                            (params.value ? `${params.value.substring(0, 8)}…` : '—')}
                    </Typography>
                );
            },
        },
        {
            field: 'current_stage',
            headerName: 'Stage',
            width: 140,
            renderCell: (params) => {
                const config = FUNNEL_STAGE_MAP[params.value as FunnelStage];
                return config ? (
                    <Chip
                        label={config.label}
                        size="small"
                        sx={{
                            backgroundColor: `${config.color}25`,
                            color: config.color,
                            border: `1px solid ${config.color}60`,
                            fontWeight: 700,
                            fontSize: '0.68rem',
                        }}
                    />
                ) : (
                    params.value
                );
            },
        },
        {
            field: 'facility_type',
            headerName: 'Facility',
            width: 120,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ color: FIYRPOD_MUTED }}>
                    {params.value ?? '—'}
                </Typography>
            ),
        },
        {
            field: 'athlete_count',
            headerName: 'Athletes',
            width: 90,
            type: 'number',
            renderCell: (params) => (
                <Typography variant="body2" sx={{ color: FIYRPOD_MUTED }}>
                    {params.value ?? '—'}
                </Typography>
            ),
        },
        {
            field: 'pipeline_value',
            headerName: 'Est. Value',
            width: 120,
            type: 'number',
            renderCell: (params) => (
                <Typography
                    variant="body2"
                    fontWeight={params.value ? 700 : 400}
                    sx={{ color: params.value ? FIYRPOD_GREEN : FIYRPOD_MUTED }}
                >
                    {params.value ? formatCurrency(params.value) : '—'}
                </Typography>
            ),
        },
        {
            field: 'days_in_current_stage',
            headerName: 'Days in Stage',
            width: 120,
            type: 'number',
            renderCell: (params) => {
                const days = params.value as number;
                return (
                    <Typography
                        variant="body2"
                        sx={{
                            color: days > 30 ? '#ef5350' : days > 14 ? '#f9a825' : '#e0e8f4',
                            fontWeight: days > 30 ? 700 : 400,
                        }}
                    >
                        {days}d
                    </Typography>
                );
            },
        },
        {
            field: 'lost_at',
            headerName: 'Status',
            width: 90,
            renderCell: (params) =>
                params.value ? (
                    <Chip
                        label="Lost"
                        size="small"
                        sx={{
                            color: '#ef5350',
                            borderColor: '#ef535060',
                            backgroundColor: 'transparent',
                            border: '1px solid',
                        }}
                    />
                ) : (
                    <Chip
                        label="Active"
                        size="small"
                        sx={{
                            color: FIYRPOD_GREEN,
                            borderColor: `${FIYRPOD_GREEN}60`,
                            backgroundColor: 'transparent',
                            border: '1px solid',
                        }}
                    />
                ),
        },
    ];

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <CircularProgress />
            </Box>
        );
    }

    const stats = dashboardStats;

    return (
        <Box sx={{ p: 3 }}>
            {/* FiyrPod branded admin header banner */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${FIYRPOD_DARK} 0%, #0f2340 100%)`,
                    border: `1px solid ${FIYRPOD_BORDER}`,
                    borderRadius: 2,
                    px: 3,
                    py: 2,
                    mb: 3,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Box
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: FIYRPOD_GREEN,
                            boxShadow: `0 0 10px ${FIYRPOD_GREEN}80`,
                        }}
                    />
                    <Box>
                        <Typography
                            sx={{
                                color: FIYRPOD_GREEN,
                                fontWeight: 900,
                                fontSize: '0.6rem',
                                letterSpacing: '0.18em',
                                textTransform: 'uppercase',
                                lineHeight: 1,
                            }}
                        >
                            FiyrPod · Admin
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                color: '#ffffff',
                                fontWeight: 700,
                                lineHeight: 1.2,
                                letterSpacing: '-0.01em',
                            }}
                        >
                            Sales Funnel Dashboard
                        </Typography>
                    </Box>
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => openAddProspectDialog(null)}
                        size="small"
                        sx={{
                            backgroundColor: FIYRPOD_GREEN,
                            color: '#0d1b2e',
                            fontWeight: 700,
                            '&:hover': { backgroundColor: '#8ed44e' },
                        }}
                    >
                        Identify Prospect for Sponsor
                    </Button>
                    <Tooltip title="Refresh">
                        <IconButton onClick={handleRefresh} sx={{ color: FIYRPOD_MUTED }}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* KPI Cards — FiyrPod dark style */}
            {stats && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {[
                        {
                            label: 'Total Prospects',
                            value: stats.total_prospects,
                            sub: `Across ${stats.sponsor_stats.length} active sponsors`,
                            accent: FIYRPOD_GREEN,
                            icon: '👥',
                        },
                        {
                            label: 'Total Pipeline Value',
                            value: formatCurrency(stats.total_pipeline_value),
                            sub: 'Combined estimated value',
                            accent: FIYRPOD_GREEN,
                            icon: '💰',
                        },
                        {
                            label: 'Overall Conversion',
                            value: `${stats.overall_conversion_rate}%`,
                            sub: 'Identified → Closed',
                            accent: FIYRPOD_GREEN,
                            icon: '✅',
                        },
                        {
                            label: 'Stale Prospects',
                            value: stats.stale_prospects_count,
                            sub: 'No movement in 30+ days',
                            accent:
                                stats.stale_prospects_count > 0 ? FIYRPOD_ORANGE : FIYRPOD_GREEN,
                            icon: stats.stale_prospects_count > 0 ? '🔥' : '✓',
                        },
                    ].map((card) => (
                        <Grid item xs={12} sm={6} md={3} key={card.label}>
                            <Paper
                                elevation={0}
                                sx={{
                                    background: `linear-gradient(135deg, ${FIYRPOD_DARK} 0%, #132238 100%)`,
                                    border: `1px solid ${FIYRPOD_BORDER}`,
                                    borderRadius: 2,
                                    p: 2.5,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '3px',
                                        background: card.accent,
                                    },
                                }}
                            >
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="flex-start"
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: FIYRPOD_MUTED,
                                            fontWeight: 600,
                                            letterSpacing: '0.05em',
                                            textTransform: 'uppercase',
                                            fontSize: '0.65rem',
                                        }}
                                    >
                                        {card.label}
                                    </Typography>
                                    <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>
                                        {card.icon}
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="h5"
                                    fontWeight={800}
                                    my={0.75}
                                    sx={{ color: card.accent, letterSpacing: '-0.01em' }}
                                >
                                    {card.value}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ color: FIYRPOD_MUTED, fontSize: '0.7rem' }}
                                >
                                    {card.sub}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Global Pipeline Bar */}
            {stats && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 2.5,
                        mb: 3,
                        background: `linear-gradient(135deg, ${FIYRPOD_DARK} 0%, #132238 100%)`,
                        border: `1px solid ${FIYRPOD_BORDER}`,
                        borderRadius: 2,
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: FIYRPOD_GREEN,
                            }}
                        />
                        <Typography
                            variant="caption"
                            sx={{
                                color: FIYRPOD_GREEN,
                                fontWeight: 700,
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                fontSize: '0.65rem',
                            }}
                        >
                            Global Pipeline
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            borderRadius: 2,
                            overflow: 'hidden',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                        }}
                    >
                        {FUNNEL_STAGES.map((stage, idx) => {
                            const count = stats.stage_counts[stage.key] || 0;
                            const total = stats.total_prospects || 1;
                            const pct = Math.max((count / total) * 100, 3);
                            const isSelected = stageFilter === stage.key;
                            return (
                                <Tooltip
                                    key={stage.key}
                                    title={`${stage.label}: ${count} prospect${
                                        count !== 1 ? 's' : ''
                                    }`}
                                >
                                    <Box
                                        onClick={() => handleStageClick(stage.key)}
                                        sx={{
                                            flex: `0 0 ${pct}%`,
                                            minWidth: 56,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            py: 2.5,
                                            px: 1,
                                            cursor: 'pointer',
                                            backgroundColor: isSelected
                                                ? stage.color
                                                : `${stage.color}60`,
                                            borderRight:
                                                idx < FUNNEL_STAGES.length - 1
                                                    ? '2px solid white'
                                                    : 'none',
                                            outline: isSelected
                                                ? `3px solid ${stage.color}`
                                                : 'none',
                                            outlineOffset: '-3px',
                                            transition: 'all 0.18s',
                                            '&:hover': { backgroundColor: stage.color },
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: '0.62rem',
                                                textAlign: 'center',
                                                lineHeight: 1.3,
                                                mb: 0.5,
                                                color: isSelected ? '#0d1b2e' : FIYRPOD_MUTED,
                                            }}
                                        >
                                            {stage.label}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: '1.4rem',
                                                lineHeight: 1,
                                                color: isSelected ? '#0d1b2e' : stage.color,
                                            }}
                                        >
                                            {count}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontSize: '0.62rem',
                                                mt: 0.25,
                                                color: isSelected
                                                    ? 'rgba(13,27,46,0.5)'
                                                    : 'rgba(255,255,255,0.3)',
                                            }}
                                        >
                                            {Math.round((count / total) * 100)}%
                                        </Typography>
                                    </Box>
                                </Tooltip>
                            );
                        })}
                    </Box>
                </Paper>
            )}

            {/* Sponsor Comparison (conditional) */}
            <Collapse in={showComparison && selectedSponsors.length >= 2}>
                <ComparisonPanel
                    sponsors={selectedSponsors}
                    onClose={() => {
                        setShowComparison(false);
                        setSelectedSponsorIds([]);
                    }}
                />
            </Collapse>

            {/* Sponsor Leaderboard + Monthly Trends */}
            {stats && (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', xl: '3fr 2fr' },
                        gap: 3,
                        mb: 3,
                    }}
                >
                    {/* Sponsor Leaderboard */}
                    <Paper
                        elevation={0}
                        sx={{
                            background: `linear-gradient(135deg, ${FIYRPOD_DARK} 0%, #132238 100%)`,
                            border: `1px solid ${FIYRPOD_BORDER}`,
                            borderRadius: 2,
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                p: 2,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                borderBottom: `1px solid ${FIYRPOD_BORDER}`,
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1}>
                                <Box
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        backgroundColor: FIYRPOD_GREEN,
                                    }}
                                />
                                <Box>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: FIYRPOD_GREEN,
                                            fontWeight: 700,
                                            letterSpacing: '0.1em',
                                            textTransform: 'uppercase',
                                            fontSize: '0.65rem',
                                        }}
                                    >
                                        Sponsor Leaderboard
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        display="block"
                                        sx={{ color: FIYRPOD_MUTED }}
                                    >
                                        Select 2+ sponsors to compare
                                    </Typography>
                                </Box>
                            </Box>
                            {selectedSponsorIds.length >= 2 && (
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<CompareArrowsIcon />}
                                    onClick={() => setShowComparison(true)}
                                    sx={{
                                        backgroundColor: FIYRPOD_GREEN,
                                        color: '#0d1b2e',
                                        fontWeight: 700,
                                        '&:hover': { backgroundColor: '#8ed44e' },
                                    }}
                                >
                                    Compare {selectedSponsorIds.length} Sponsors
                                </Button>
                            )}
                        </Box>
                        <DataGridPremium
                            rows={stats.sponsor_stats}
                            columns={leaderboardColumns}
                            getRowId={(row) => row.organization_uuid}
                            checkboxSelection
                            disableRowSelectionOnClick
                            rowSelectionModel={selectedSponsorIds}
                            onRowSelectionModelChange={(model) => {
                                setSelectedSponsorIds(model);
                                if (model.length < 2) setShowComparison(false);
                            }}
                            initialState={{
                                sorting: { sortModel: [{ field: 'closed_count', sort: 'desc' }] },
                            }}
                            hideFooter
                            autoHeight
                            density="compact"
                            sx={darkGridSx}
                        />
                    </Paper>

                    {/* Monthly Trends */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.5,
                            background: `linear-gradient(135deg, ${FIYRPOD_DARK} 0%, #132238 100%)`,
                            border: `1px solid ${FIYRPOD_BORDER}`,
                            borderRadius: 2,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <Box
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: FIYRPOD_GREEN,
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{
                                    color: FIYRPOD_GREEN,
                                    fontWeight: 700,
                                    letterSpacing: '0.1em',
                                    textTransform: 'uppercase',
                                    fontSize: '0.65rem',
                                }}
                            >
                                Monthly Trends
                            </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: FIYRPOD_MUTED }} mb={2}>
                            Prospects added vs. closed (last 6 months)
                        </Typography>
                        {stats.monthly_trends.length > 0 ? (
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart
                                    data={stats.monthly_trends.map((t) => ({
                                        ...t,
                                        monthLabel: formatMonth(t.month),
                                    }))}
                                    margin={{ top: 4, right: 8, left: -10, bottom: 0 }}
                                >
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        vertical={false}
                                        stroke={FIYRPOD_BORDER}
                                    />
                                    <XAxis
                                        dataKey="monthLabel"
                                        tick={{ fontSize: 11, fill: FIYRPOD_MUTED }}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 11, fill: FIYRPOD_MUTED }}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <RechartsTooltip
                                        contentStyle={{
                                            borderRadius: 8,
                                            border: `1px solid ${FIYRPOD_BORDER}`,
                                            backgroundColor: FIYRPOD_DARK,
                                            color: '#e0e8f0',
                                            fontSize: 12,
                                        }}
                                        labelStyle={{ color: FIYRPOD_GREEN, fontWeight: 700 }}
                                    />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    <Bar
                                        dataKey="prospects_added"
                                        name="Added"
                                        fill={`${FIYRPOD_GREEN}60`}
                                        radius={[4, 4, 0, 0]}
                                    />
                                    <Bar
                                        dataKey="closed_count"
                                        name="Closed"
                                        fill={FIYRPOD_GREEN}
                                        radius={[4, 4, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                flex={1}
                            >
                                <Typography variant="body2" sx={{ color: FIYRPOD_MUTED }}>
                                    No trend data yet
                                </Typography>
                            </Box>
                        )}

                        {/* Overall Stage Breakdown mini */}
                        <Box mt={2} pt={2} sx={{ borderTop: `1px solid ${FIYRPOD_BORDER}` }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: FIYRPOD_MUTED,
                                    fontWeight: 600,
                                    fontSize: '0.65rem',
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                }}
                                display="block"
                                mb={1}
                            >
                                Stage Distribution — All Sponsors
                            </Typography>
                            {FUNNEL_STAGES.map((stage) => {
                                const count = stats.stage_counts[stage.key] || 0;
                                const total = stats.total_prospects || 1;
                                const pct = Math.round((count / total) * 100);
                                return (
                                    <Box key={stage.key} mb={0.75}>
                                        <Box display="flex" justifyContent="space-between" mb={0.2}>
                                            <Typography
                                                variant="caption"
                                                sx={{ fontSize: '0.68rem', color: FIYRPOD_MUTED }}
                                            >
                                                {stage.label}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                fontWeight={600}
                                                sx={{ fontSize: '0.68rem', color: stage.color }}
                                            >
                                                {count} ({pct}%)
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={pct}
                                            sx={{
                                                height: 5,
                                                borderRadius: 3,
                                                backgroundColor: 'rgba(255,255,255,0.06)',
                                                '& .MuiLinearProgress-bar': {
                                                    backgroundColor: stage.color,
                                                    borderRadius: 3,
                                                },
                                            }}
                                        />
                                    </Box>
                                );
                            })}
                        </Box>
                    </Paper>
                </Box>
            )}

            {/* All Prospects Table */}
            <Paper
                elevation={0}
                sx={{
                    background: `linear-gradient(135deg, ${FIYRPOD_DARK} 0%, #132238 100%)`,
                    border: `1px solid ${FIYRPOD_BORDER}`,
                    borderRadius: 2,
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        display: 'flex',
                        gap: 2,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        borderBottom: `1px solid ${FIYRPOD_BORDER}`,
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1} sx={{ mr: 1 }}>
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: FIYRPOD_GREEN,
                            }}
                        />
                        <Typography
                            variant="caption"
                            sx={{
                                color: FIYRPOD_GREEN,
                                fontWeight: 700,
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                fontSize: '0.65rem',
                            }}
                        >
                            All Prospects
                        </Typography>
                    </Box>
                    <TextField
                        size="small"
                        placeholder="Search prospects or contacts..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                setPaginationModel((p) => ({ ...p, page: 0 }));
                                loadProspects();
                            }
                        }}
                        sx={{
                            width: 280,
                            '& .MuiOutlinedInput-root': {
                                color: '#d0dae8',
                                '& fieldset': { borderColor: FIYRPOD_BORDER },
                                '&:hover fieldset': { borderColor: FIYRPOD_GREEN },
                                '&.Mui-focused fieldset': { borderColor: FIYRPOD_GREEN },
                            },
                            '& .MuiInputBase-input::placeholder': {
                                color: FIYRPOD_MUTED,
                                opacity: 1,
                            },
                            '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: FIYRPOD_MUTED },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        select
                        size="small"
                        label="Stage"
                        value={stageFilter}
                        onChange={(e) => {
                            setStageFilter(e.target.value as FunnelStage | '');
                            setPaginationModel((p) => ({ ...p, page: 0 }));
                        }}
                        sx={{
                            width: 160,
                            '& .MuiOutlinedInput-root': {
                                color: '#d0dae8',
                                '& fieldset': { borderColor: FIYRPOD_BORDER },
                                '&:hover fieldset': { borderColor: FIYRPOD_GREEN },
                                '&.Mui-focused fieldset': { borderColor: FIYRPOD_GREEN },
                            },
                            '& .MuiInputLabel-root': { color: FIYRPOD_MUTED },
                            '& .MuiInputLabel-root.Mui-focused': { color: FIYRPOD_GREEN },
                            '& .MuiSelect-icon': { color: FIYRPOD_MUTED },
                        }}
                    >
                        <MenuItem value="">All Stages</MenuItem>
                        {FUNNEL_STAGES.map((s) => (
                            <MenuItem key={s.key} value={s.key}>
                                {s.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Chip
                        icon={<WarningAmberIcon />}
                        label="Stale Only"
                        variant={staleFilter ? 'filled' : 'outlined'}
                        onClick={() => {
                            setStaleFilter((v) => !v);
                            setPaginationModel((p) => ({ ...p, page: 0 }));
                        }}
                        sx={{
                            cursor: 'pointer',
                            height: 32,
                            color: staleFilter ? '#0d1b2e' : FIYRPOD_MUTED,
                            backgroundColor: staleFilter ? FIYRPOD_ORANGE : 'transparent',
                            borderColor: staleFilter ? FIYRPOD_ORANGE : FIYRPOD_BORDER,
                            '& .MuiChip-icon': {
                                color: staleFilter ? '#0d1b2e' : FIYRPOD_ORANGE,
                            },
                        }}
                    />
                    <Typography variant="caption" sx={{ ml: 'auto', color: FIYRPOD_MUTED }}>
                        {totalRows} prospects
                    </Typography>
                </Box>
                <DataGridPremium
                    rows={prospects}
                    columns={prospectsColumns}
                    getRowId={(row) => row.uuid}
                    loading={tableLoading}
                    rowCount={totalRows}
                    paginationMode="server"
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    pageSizeOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                    autoHeight
                    sx={darkGridSx}
                />
            </Paper>
            {/* Admin Add Prospect Dialog — uses eligible Gold sponsors, not just active funnel orgs */}
            <AdminAddProspectDialog
                open={addProspectOpen}
                onClose={() => {
                    setAddProspectOpen(false);
                    setAddProspectDefaultOrg(null);
                }}
                sponsors={eligibleSponsors}
                defaultOrgUuid={addProspectDefaultOrg}
                onCreated={() => {
                    loadDashboard();
                    loadProspects();
                }}
            />
        </Box>
    );
};

export default AdminSalesFunnelDashboard;
